import { useRouter } from '@tanstack/react-router';
import { mapApiErrorToForm } from '@upme/api-client';
import type {
  CityDto,
  EventCategoryDto,
  EventCreateInput,
  EventDto,
  EventUpdateInput,
} from '@upme/contracts';
import { EventCreateInput as ECrSchema, EventUpdateInput as EUpSchema } from '@upme/contracts';
import { Button } from '@upme/ui/components/button';
import { Input } from '@upme/ui/components/input';
import { Label } from '@upme/ui/components/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@upme/ui/components/select';
import { useMemo } from 'react';
import { type Resolver, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import type { z } from 'zod';
import { collectRootMessages } from '../../../app/form-errors';
import { zodResolver } from '../../../app/zod-resolver';
import { PlaceCombobox } from '../../places/components/place-combobox';
import { toLocalDateTimeParts } from '../lib/format';
import { useCreateEvent, useUpdateEvent } from '../mutations';
import { CategoryMultiSelect } from './category-multi-select';

type CreateProps = {
  mode: 'create';
  cities: ReadonlyArray<CityDto>;
  categories: ReadonlyArray<EventCategoryDto>;
};
type EditProps = {
  mode: 'edit';
  cities: ReadonlyArray<CityDto>;
  categories: ReadonlyArray<EventCategoryDto>;
  event: EventDto;
};
type Props = CreateProps | EditProps;

type FormValues = {
  cityId: string;
  title: string;
  description?: string | undefined;
  venueText: string;
  placeId?: string | null | undefined;
  startsAtLocalDate: string;
  startsAtLocalTime: string | null;
  endsAtLocalDate?: string | undefined;
  endsAtLocalTime?: string | undefined;
  price?: number | null | undefined;
  maxAttendees?: number | null | undefined;
  organizerUsername?: string | undefined;
  coverImageUrl?: string | undefined;
  videoUrl?: string | undefined;
  categoryIds: string[];
  publish?: boolean | undefined;
  bookingExtraInfo?: string | undefined;
  reminderText?: string | undefined;
  isVerified?: boolean | undefined;
  isRecommended?: boolean | undefined;
};

export function EventForm(props: Props) {
  const router = useRouter({ warn: false });
  const isCreate = props.mode === 'create';

  const defaults = useMemo<Partial<FormValues>>(() => {
    if (isCreate) {
      const city = props.cities[0];
      return {
        cityId: city?.id ?? '',
        title: '',
        venueText: '',
        placeId: null,
        startsAtLocalDate: '',
        startsAtLocalTime: '',
        categoryIds: [],
        publish: false,
      };
    }
    const e = props.event;
    const city = props.cities.find((c) => c.id === e.cityId);
    const tz = city?.timezone ?? 'UTC';
    const start = toLocalDateTimeParts(e.startsAt, tz);
    const end = e.endsAt ? toLocalDateTimeParts(e.endsAt, tz) : undefined;
    return {
      cityId: e.cityId,
      title: e.title,
      description: e.description ?? undefined,
      venueText: e.venueText ?? '',
      placeId: e.place?.id ?? null,
      startsAtLocalDate: start.date,
      startsAtLocalTime: e.hasStartTime ? start.time : '',
      endsAtLocalDate: end?.date,
      endsAtLocalTime: end?.time,
      price: e.price,
      maxAttendees: e.maxAttendees ?? undefined,
      organizerUsername: e.organizerUsername ?? undefined,
      coverImageUrl: e.coverImageUrl ?? undefined,
      videoUrl: e.videoUrl ?? undefined,
      categoryIds: e.categories.map((c) => c.id),
      bookingExtraInfo: e.bookingExtraInfo ?? undefined,
      reminderText: e.reminderText ?? undefined,
      isVerified: e.isVerified,
      isRecommended: e.isRecommended,
    };
  }, [isCreate, props]);

  const schema = isCreate ? ECrSchema : EUpSchema;

  const form = useForm<FormValues>({
    resolver: zodResolver(schema as unknown as z.ZodType<FormValues>) as Resolver<FormValues>,
    defaultValues: defaults as FormValues,
    mode: 'onSubmit',
  });

  const create = useCreateEvent();
  const update = useUpdateEvent(isCreate ? '' : props.event.id);

  const cityId = form.watch('cityId');
  const city = props.cities.find((c) => c.id === cityId);
  const tzLabel = city ? `${city.timezone}` : '';
  const categoryIds = form.watch('categoryIds') ?? [];

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      const payload: EventCreateInput | EventUpdateInput = {
        ...values,
        // Empty strings → undefined so optional URL/text fields don't fail validation.
        description: values.description || undefined,
        organizerUsername: values.organizerUsername || undefined,
        coverImageUrl: values.coverImageUrl || undefined,
        videoUrl: values.videoUrl || undefined,
        startsAtLocalTime: values.startsAtLocalTime ? values.startsAtLocalTime : null,
        endsAtLocalDate: values.endsAtLocalDate || undefined,
        endsAtLocalTime: values.endsAtLocalTime || undefined,
        price: values.price === undefined || Number.isNaN(values.price) ? null : values.price,
        maxAttendees:
          values.maxAttendees === undefined || Number.isNaN(values.maxAttendees) ? null : values.maxAttendees,
        bookingExtraInfo: values.bookingExtraInfo || undefined,
        reminderText: values.reminderText || undefined,
      } as EventCreateInput;

      const result = isCreate
        ? await create.mutateAsync(payload as EventCreateInput)
        : await update.mutateAsync(payload as EventUpdateInput);
      toast.success(isCreate ? 'Событие создано' : 'Изменения сохранены');
      router?.navigate({ to: '/events/$eventId', params: { eventId: result.id } });
    } catch (err) {
      await mapApiErrorToForm(err, form);
    }
  });

  const err = (k: keyof FormValues) => {
    const e = form.formState.errors[k] as { message?: string } | undefined;
    return e?.message ? <p className="text-sm text-destructive mt-1">{String(e.message)}</p> : null;
  };
  const rootMessages = collectRootMessages(form.formState.errors as Record<string, unknown>);

  return (
    <form onSubmit={onSubmit} className="space-y-5 max-w-3xl" noValidate>
      <div>
        <Label>Город</Label>
        <Select
          value={cityId}
          onValueChange={(v) => {
            form.setValue('cityId', v, { shouldValidate: true });
            // Комбобокс мест ищет в выбранном городе, но само выбранное место
            // в поле остаётся — и уезжает в PATCH вместе с новым городом.
            // Бэкенд такую пару отклоняет («place is in another city»), да и
            // молча принятая она делала событие неправимым для организатора.
            // venueText остаётся: это свободный текст, а не ссылка на каталог.
            if (v !== cityId) form.setValue('placeId', null);
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder="Выберите город" />
          </SelectTrigger>
          <SelectContent>
            {props.cities.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {tzLabel && (
          <p className="text-xs text-muted-foreground mt-1">
            Время вводится в часовом поясе города: <code>{tzLabel}</code>
          </p>
        )}
        {err('cityId')}
      </div>

      <div>
        <Label htmlFor="title">Название</Label>
        <Input id="title" {...form.register('title')} placeholder="Поэтический slam" />
        {err('title')}
      </div>

      <div>
        <Label>Место (опционально)</Label>
        <PlaceCombobox
          cityId={cityId}
          value={form.watch('placeId')}
          onChange={(id, place) => {
            form.setValue('placeId', id);
            if (place) form.setValue('venueText', place.name);
          }}
        />
        {err('placeId')}
      </div>

      <div>
        <Label htmlFor="venueText">Текст места</Label>
        <Input id="venueText" {...form.register('venueText')} placeholder="Бар на Бенх-Тань" />
        {err('venueText')}
      </div>

      <div>
        <Label htmlFor="description">Описание</Label>
        <textarea
          id="description"
          rows={4}
          className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-xs focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring"
          {...form.register('description')}
        />
        {err('description')}
      </div>

      <div className="grid gap-3 grid-cols-1 md:grid-cols-2">
        <div>
          <Label htmlFor="startsAtLocalDate">Дата начала</Label>
          <Input id="startsAtLocalDate" type="date" {...form.register('startsAtLocalDate')} />
          {err('startsAtLocalDate')}
        </div>
        <div>
          <Label htmlFor="startsAtLocalTime">Время начала (опц.)</Label>
          <Input id="startsAtLocalTime" type="time" {...form.register('startsAtLocalTime')} />
          {err('startsAtLocalTime')}
        </div>
        <div>
          <Label htmlFor="endsAtLocalDate">Дата конца (опц.)</Label>
          <Input id="endsAtLocalDate" type="date" {...form.register('endsAtLocalDate')} />
          {err('endsAtLocalDate')}
        </div>
        <div>
          <Label htmlFor="endsAtLocalTime">Время конца (опц.)</Label>
          <Input id="endsAtLocalTime" type="time" {...form.register('endsAtLocalTime')} />
          {err('endsAtLocalTime')}
        </div>
      </div>

      <div>
        <Label>Категории</Label>
        <CategoryMultiSelect
          categories={props.categories}
          value={categoryIds}
          onChange={(next) => form.setValue('categoryIds', next, { shouldValidate: true })}
        />
        {err('categoryIds')}
      </div>

      <div className="grid gap-3 grid-cols-1 md:grid-cols-2">
        <div>
          <Label htmlFor="price">Цена, в основных единицах (0 / пусто = бесплатно)</Label>
          <Input
            id="price"
            type="number"
            min={0}
            step={1}
            {...form.register('price', { valueAsNumber: true })}
          />
          {err('price')}
        </div>
        <div>
          <Label htmlFor="maxAttendees">Лимит участников (пусто = без лимита)</Label>
          <Input
            id="maxAttendees"
            type="number"
            min={1}
            step={1}
            {...form.register('maxAttendees', { valueAsNumber: true })}
          />
          {err('maxAttendees')}
        </div>
        <div>
          <Label htmlFor="organizerUsername">Telegram-юзернейм организатора (опц.)</Label>
          <Input id="organizerUsername" {...form.register('organizerUsername')} placeholder="@ada_lovelace" />
          {err('organizerUsername')}
        </div>
        <div>
          <Label htmlFor="coverImageUrl">Обложка (URL)</Label>
          <Input id="coverImageUrl" {...form.register('coverImageUrl')} placeholder="https://…" />
          {err('coverImageUrl')}
        </div>
        <div>
          <Label htmlFor="videoUrl">Видео (URL)</Label>
          <Input id="videoUrl" {...form.register('videoUrl')} placeholder="https://…" />
          {err('videoUrl')}
        </div>
      </div>

      <div>
        <Label htmlFor="bookingExtraInfo">Доп. инфо при брони (опц.)</Label>
        <textarea
          id="bookingExtraInfo"
          rows={3}
          className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-xs focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring"
          {...form.register('bookingExtraInfo')}
        />
        {err('bookingExtraInfo')}
      </div>

      <div>
        <Label htmlFor="reminderText">Текст напоминания за ~1 ч (опц.)</Label>
        <textarea
          id="reminderText"
          rows={3}
          className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-xs focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring"
          {...form.register('reminderText')}
        />
        {err('reminderText')}
      </div>

      {!isCreate && (
        <div className="flex flex-col gap-2 rounded-md border p-3">
          <div className="font-medium text-sm">Статусы в ленте</div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" className="h-4 w-4" {...form.register('isRecommended')} />
            Рекомендовано
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" className="h-4 w-4" {...form.register('isVerified')} />
            Проверено
          </label>
        </div>
      )}

      {isCreate && (
        <div className="flex items-center gap-2">
          <input id="publish" type="checkbox" className="h-4 w-4" {...form.register('publish')} />
          <Label htmlFor="publish">Опубликовать сразу (иначе создастся как черновик)</Label>
        </div>
      )}

      {rootMessages.map((m) => (
        <p key={m} className="text-sm text-destructive">
          {m}
        </p>
      ))}

      <Button type="submit" disabled={form.formState.isSubmitting}>
        {isCreate ? 'Создать' : 'Сохранить'}
      </Button>
    </form>
  );
}
